import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {MatDialog} from '@angular/material/dialog';
import {MatExpansionPanel} from '@angular/material/expansion';
import {AddRemarkPopup} from '../../../../popups/add-remark-popup/add-remark-popup.component';
import {ConfirmPopupDialog} from '../../../../popups/confirm-popup/confirm-popup.component';
import {
    IPackage_ProductAllocation,
    IProduct_ProductAllocation,
    PackingTypeNew,
    UnitsOfMeasure
} from '../../product-allocation-new.component';
import {moreThen} from '../pack-items/pack-items.component';

@Component({
    selector: 'eci-pallet-item',
    templateUrl: './pallet-item.component.html',
    styleUrls: ['./pallet-item.component.scss']
})
export class PalletItemComponent implements OnInit {

    @Input() package: IPackage_ProductAllocation;
    @Output() packageChange: EventEmitter<IPackage_ProductAllocation> = new EventEmitter<IPackage_ProductAllocation>();

    @Input() isSimply: boolean = false;
    @Input() notAllocated: number = 0;
    @Input() denominator: number = 1;
    @Input() canCancel: boolean = false;
    @Input() products: IProduct_ProductAllocation[] = [];
    @Input() isViewing: boolean = false;
    @Input() selected: boolean = false;
    @Output() onCancelEvent: EventEmitter<IPackage_ProductAllocation> = new EventEmitter<IPackage_ProductAllocation>();
    @Output() onQuantityChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() onDuplicateChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() selectionChange: EventEmitter<MatCheckboxChange> = new EventEmitter<MatCheckboxChange>();

    @ViewChild(MatExpansionPanel) matExpansionPanel: MatExpansionPanel;

    palletForm: FormGroup;
    validate = false;
    lastCartonIndex = 0;

    constructor(
        private formBuilder: FormBuilder,
        private dialog: MatDialog,
    ) {
    }
    get productsInPackage() {
        return this.package.cartons.reduce((sum, value) => {
            return sum + value.products.length;
        }, 0) | 0;
    }

    get productQuantity() {
        return this.package.cartons.reduce((sum, value) => {
            const cartonQuantity = value.products.reduce((pSum, pValue) => {
                return pSum + pValue.allocated;
            }, 0);
            return sum + cartonQuantity;
        }, 0) | 0;
    }

    ngOnInit(): void {
        this.palletForm = this.formBuilder.group({
            length: [{value: this.package.length, disabled: this.isViewing}, {validators: [Validators.required, moreThen()]}],
            width: [{value: this.package.width, disabled: this.isViewing}, {validators: [Validators.required, moreThen()]}],
            height: [{value: this.package.height, disabled: this.isViewing}, {validators: [Validators.required, moreThen()]}],
            grossWeight: [{value: this.package.grossWeight, disabled: this.isViewing}, {validators: [Validators.required, moreThen()]}],
            volumetricWeight: [this.package.grossWeight],
        });

        this.palletForm.get('length').valueChanges.subscribe(values => this.onChangeInfo());
        this.palletForm.get('width').valueChanges.subscribe(values => this.onChangeInfo());
        this.palletForm.get('height').valueChanges.subscribe(values => this.onChangeInfo());

        if (this.package.cartons.length) {
            this.lastCartonIndex = Math.max(...this.package.cartons.map(c => c.index || 0));
        } else {
            this.addChild();
        }
    }

    addChild() {
        this.package.cartons.push(
            {
                packingType: PackingTypeNew.carton,
                productsQuantity: 0,
                length: 0,
                width: 0,
                height: 0,
                cartons_qty: 0,
                unitsOfMeasure: UnitsOfMeasure.Kg,
                grossWeight: 0,
                volumetricWeight: 0,
                expand: true,
                products: [
                    {item: {}, allocated: 0}
                ],
                index: ++this.lastCartonIndex,
                isNew: true,
            }
        )
        this.onChangeInfo();
    }

    onSave() {
        if (this.validatePackage()) {
            this.package = {
                ...this.package,
                ...this.palletForm.value,
                productsQuantity: this.productQuantity,
                expand: false,
                isNew: false
            };
            this.packageChange.emit(this.package);
            this.matExpansionPanel.close();
        }
    }

    onCancel() {
        this.onCancelEvent.emit(this.package);
    }

    onPanelClosed() {
        if (this.package.expand && !this.isViewing) {
            this.matExpansionPanel.open();
        }
    }

    private validatePackage() {
        this.validate = true;
        if (!this.palletForm.valid) {
            return false;
        }
        if (this.isSimply) {
            return this.notAllocated >= 0;
        }
        return !this.products.some(p => p.notAllocated < 0);
    }

    onChangeInfo() {
        this.palletForm.get('volumetricWeight').setValue(this.calculateVolumetricWeight());
    }

    calculateVolumetricWeight() {
        const volumetricWeight =
            this.palletForm.get('height').value *
            this.palletForm.get('width').value *
            this.palletForm.get('length').value *
            this.package.cartons.length / this.denominator;
        return parseFloat(volumetricWeight.toFixed(3));
    }

    onCancelPack(event: IPackage_ProductAllocation, index: number) {
        this.package.cartons.splice(index, 1);
        this.lastCartonIndex = 0;

        this.package.cartons.map(pl => {
            pl.index = ++this.lastCartonIndex;
        })
        this.package.productsQuantity = this.productQuantity;
        this.packageChange.emit(this.package);
    }

    onDuplicate(e: { package: IPackage_ProductAllocation, count: number }) {
        const pkg = e.package;
        for (let i = 0; i < e.count; i++) {
            this.package.cartons.push({
                packingType: PackingTypeNew.carton,
                productsQuantity: pkg.productsQuantity,
                length: pkg.length,
                width: pkg.width,
                height: pkg.height,
                cartons_qty: pkg.cartons_qty || 0,
                unitsOfMeasure: pkg.unitsOfMeasure || UnitsOfMeasure.Kg,
                grossWeight: pkg.grossWeight,
                volumetricWeight: pkg.volumetricWeight,
                expand: false,
                isNew: false,
                products: pkg.products.map(p => ({
                    item: p.item ? {...p.item} : p.item,
                    allocated: p.allocated
                })),
                index: ++this.lastCartonIndex,
            });
        }
        this.package.productsQuantity = this.productQuantity;
        this.onChangeInfo();
        this.packageChange.emit(this.package);
    }

    onDuplicatePallet(e: MouseEvent) {
        e.stopPropagation();
        e.preventDefault();
        const dialogRef = this.dialog.open(AddRemarkPopup, {width: '400px'});
        dialogRef.componentInstance.dialogTitle = 'Duplicate pallet';
        dialogRef.componentInstance.fieldLabel = 'Number of copies';
        dialogRef.componentInstance.numericOnly = true;
        dialogRef.afterOpened().subscribe(() => {
            dialogRef.componentInstance.form.patchValue({remark: '1'});
        });
        dialogRef.afterClosed().subscribe(value => {
            const count = parseInt(value, 10);
            if (!count || count < 1) {
                return;
            }
            if (this.isSimply) {
                const qty = this.package.productsQuantity || this.productQuantity || 0;
                if (count * qty > this.notAllocated) {
                    const errRef = this.dialog.open(ConfirmPopupDialog, {width: '400px'});
                    errRef.componentInstance.header = 'Cannot duplicate';
                    errRef.componentInstance.message = `Only ${this.notAllocated} allocated product quantity available.`;
                    errRef.componentInstance.showRightBtn = false;
                    errRef.componentInstance.leftBtnText = 'OK';
                    return;
                }
            } else {
                for (const carton of this.package.cartons || []) {
                    for (const p of carton.products || []) {
                        if (!p.item?.productId) {
                            continue;
                        }
                        const product = this.products.find(pr => pr.productId === p.item.productId);
                        const needed = count * (p.allocated || 0);
                        if (product && needed > product.notAllocated) {
                            const errRef = this.dialog.open(ConfirmPopupDialog, {width: '400px'});
                            errRef.componentInstance.header = 'Cannot duplicate';
                            errRef.componentInstance.message = `Not enough units for ${product.description || product.itemNumber}. Available: ${product.notAllocated}, needed: ${needed}.`;
                            errRef.componentInstance.showRightBtn = false;
                            errRef.componentInstance.leftBtnText = 'OK';
                            return;
                        }
                    }
                }
            }
            this.onDuplicateChange.emit({package: this.package, count});
        });
    }

    getUnit(unitsOfMeasure: number) {
        return UnitsOfMeasure[unitsOfMeasure];
    }

    updatePackage(data: IPackage_ProductAllocation, index: number) {
        for (const key in this.package.cartons[index]) {
            this.package.cartons[index][key] = data[key];
        }
        this.package.productsQuantity = this.productQuantity;
    }

    updateQuantity(e: any, index: number) {
        if (this.isSimply) {
            this.package.cartons[index].products[0].allocated = e.value;
        } else {
            const next = [];
            for (const v of e.value) {
                const productId = Number(Object.keys(v)[0]);
                const product = this.package.cartons[index].products.find(p => Number(p?.item?.productId) === productId);
                if (product) {
                    product.allocated = v[productId];
                    next.push(product);
                } else {
                    next.push({
                        item:{
                            productId
                        },
                        allocated: v[productId]
                    })
                }
            }
            this.package.cartons[index].products = next;
        }
        this.package.productsQuantity = this.productQuantity;
        this.onQuantityChange.emit(e);
    }
}
